import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, Loader2, Share2, UserPlus2, Send, CheckCircle2, Trash2, Sparkles, TrendingUp, Users, Repeat, Gift, Upload, Mail, Megaphone } from "lucide-react";
import { CopyStudio } from "../components/CopyStudio";

const STATUS = {
  generating: { label: "Designing", tint: "bg-slate-500/15 text-slate-300" },
  generated: { label: "Ready", tint: "bg-emerald-500/15 text-emerald-300" },
  shared: { label: "Shared", tint: "bg-blue-500/15 text-blue-300" },
  nominated: { label: "Nominated", tint: "bg-amber-500/15 text-amber-300" },
  invited: { label: "Invited", tint: "bg-purple-500/15 text-purple-300" },
  joined: { label: "Joined", tint: "bg-emerald-500/20 text-emerald-300" },
  failed: { label: "Failed", tint: "bg-red-500/15 text-red-300" },
};

export const CoverChain = () => {
  const [covers, setCovers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [subject, setSubject] = useState("");
  const [biz, setBiz] = useState("");
  const [file, setFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [c, m] = await Promise.all([api.listCovers(), api.coverMetrics()]);
    setCovers(c); setMetrics(m);
  };
  useEffect(() => { load(); }, []);

  // poll while any cover is still rendering
  useEffect(() => {
    if (!covers.some((c) => c.cover_pending)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [covers]); // eslint-disable-line

  const create = async () => {
    if (!file || !subject.trim()) { toast.error("Add a photo and a name."); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("subject_name", subject.trim());
      fd.append("business_name", biz.trim());
      await api.createCover(fd);
      setSubject(""); setBiz(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
      toast.success("KURT is designing the cover — it'll appear in a moment.");
    } catch { toast.error("Couldn't start the cover. Try a smaller image."); }
    finally { setCreating(false); }
  };

  const m = metrics;

  return (
    <div data-testid="screen-covers" className="max-w-6xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">COVER CHAIN</p>
        <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">10,000 SMEs. 10,000 covers.</h1>
        <p className="text-slate-400 mt-2 text-sm">Put Britain's small businesses on the cover. Upload → generate → share → nominate → invite → repeat. A viral acquisition loop KURT tracks end to end.</p>
      </div>

      {m && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <Stat icon={ImagePlus} label="Covers created" value={m.total_covers} accent="text-emerald-400" sub={`${m.generated} ready`} testid="cm-total" />
          <Stat icon={Repeat} label="Viral coefficient" value={m.viral_coefficient} accent="text-blue-400" sub="new covers per cover" testid="cm-viral" />
          <Stat icon={Users} label="Leads generated" value={m.leads_generated} accent="text-amber-400" sub={`${m.total_nominees} nominated`} testid="cm-leads" />
          <Stat icon={TrendingUp} label="Members joined" value={m.membership_conversions} accent="text-emerald-400" sub={`CAC ${m.cac}`} testid="cm-joined" />
        </div>
      )}

      {/* Upload / generate */}
      <div className="rounded-2xl border border-white/10 bg-[#121824] p-5 mt-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="flex-1">
            <label className="text-xs text-slate-400 font-medium">Cover star photo</label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={fileRef} data-testid="cover-file-input" type="file" accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden" id="cover-file" />
              <label htmlFor="cover-file" className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/10 bg-[#0B0F17] text-sm text-slate-300 cursor-pointer hover:border-emerald-500/40 transition-all">
                <Upload size={15} className="text-emerald-400" /> {file ? "Change photo" : "Choose photo"}
              </label>
              {file && <span className="text-xs text-slate-400 truncate max-w-[160px]">{file.name}</span>}
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-slate-400 font-medium">Name</label>
            <input data-testid="cover-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Founder / owner name"
              className="mt-1.5 w-full bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40 transition-all" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-slate-400 font-medium">Business (optional)</label>
            <input data-testid="cover-business-input" value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Business name"
              className="mt-1.5 w-full bg-[#0B0F17] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40 transition-all" />
          </div>
          <button data-testid="create-cover-btn" onClick={create} disabled={creating}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate cover
          </button>
        </div>
      </div>

      {/* Gallery */}
      {covers.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-10 text-center mt-4">
          <ImagePlus size={26} className="text-emerald-400 mx-auto" />
          <p className="kz-head text-lg font-bold mt-3">No covers yet.</p>
          <p className="text-slate-400 text-sm mt-1">Upload a founder's photo and KURT will make them a cover star.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {covers.map((c) => <CoverCard key={c.id} c={c} onChange={load} />)}
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
    <Icon size={15} className="text-slate-500" />
    <div className={`kz-head text-2xl font-extrabold mt-2 ${accent}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
    {sub && <div className="text-[10px] mt-1 text-slate-500">{sub}</div>}
  </div>
);

const CoverCard = ({ c, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [nomOpen, setNomOpen] = useState(false);
  const [nom, setNom] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const st = STATUS[c.status] || STATUS.generating;

  const act = async (fn, msg) => { setBusy(true); try { await fn(); await onChange(); msg && toast.success(msg); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } finally { setBusy(false); } };
  const nominate = async () => {
    const names = nom.split(",").map((n) => n.trim()).filter(Boolean);
    if (!names.length) return;
    await act(() => api.nominateCover(c.id, names), `Nominated ${names.length} SME owner(s).`);
    setNom(""); setNomOpen(false);
  };
  const saveInvite = (v) => api.editCoverCopy(c.id, { invite_copy: v }).then(() => onChange());
  const saveNomination = (v) => api.editCoverCopy(c.id, { nomination_copy: v }).then(() => onChange());

  return (
    <div data-testid={`cover-${c.id}`} className="rounded-2xl border border-white/10 bg-[#121824] overflow-hidden flex flex-col kz-fade-up">
      <div className="relative bg-[#0B0F17] aspect-[3/4] flex items-center justify-center">
        {c.cover_pending ? (
          <div data-testid={`cover-pending-${c.id}`} className="flex flex-col items-center gap-2 text-slate-400 text-sm">
            <Loader2 size={20} className="text-emerald-400 animate-spin" /> Designing cover…
          </div>
        ) : c.cover_path ? (
          <img src={`${API}/covers/${c.id}/image?kind=cover`} alt={`${c.subject_name} cover`} data-testid={`cover-img-${c.id}`} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="text-red-300 text-sm">Cover failed</div>
        )}
        <span className={`absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${st.tint}`}>{st.label}</span>
      </div>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <p className="kz-head font-bold text-slate-100 truncate">{c.subject_name}</p>
          {c.business_name && <p className="text-xs text-slate-400 truncate">{c.business_name}</p>}
          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
            <span>{c.shares} shares</span>
            <span>{(c.nominees || []).length} nominated</span>
          </div>
        </div>

        {nomOpen && (
          <div className="flex gap-1.5">
            <input data-testid={`nom-input-${c.id}`} value={nom} onChange={(e) => setNom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && nominate()} placeholder="Names, comma-separated"
              className="flex-1 bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-emerald-500/40" />
            <button data-testid={`nom-save-${c.id}`} onClick={nominate} disabled={busy} className="text-[11px] font-semibold px-2.5 rounded-lg bg-emerald-500 text-[#04140d]">Add</button>
          </div>
        )}

        <div className="mt-auto grid grid-cols-2 gap-1.5">
          <Btn testid={`share-${c.id}`} onClick={() => act(() => api.shareCover(c.id), "Shared.")} disabled={busy || c.cover_pending} icon={Share2}>Share</Btn>
          <Btn testid={`nominate-${c.id}`} onClick={() => setNomOpen(!nomOpen)} disabled={busy || c.cover_pending} icon={UserPlus2}>Nominate</Btn>
          {!c.invited ? (
            <Btn testid={`invite-${c.id}`} onClick={() => act(() => api.inviteCover(c.id), "Invitation sent.")} disabled={busy || c.cover_pending} icon={Send}>Invite</Btn>
          ) : !c.joined ? (
            <Btn testid={`join-${c.id}`} onClick={() => act(() => api.joinCover(c.id), "Joined 🎉")} disabled={busy} icon={CheckCircle2} accent>Mark joined</Btn>
          ) : (
            <span className="flex items-center justify-center gap-1 text-[11px] font-semibold text-emerald-400 rounded-lg bg-emerald-500/10 py-1.5"><Gift size={13} /> Member</span>
          )}
          <Btn testid={`del-cover-${c.id}`} onClick={() => act(() => api.deleteCover(c.id))} disabled={busy} icon={Trash2} danger>Delete</Btn>
        </div>

        <button data-testid={`copy-toggle-${c.id}`} onClick={() => setCopyOpen(!copyOpen)}
          className="mt-1 flex items-center justify-center gap-1 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">
          <Sparkles size={12} /> {copyOpen ? "Hide" : "KURT's ready-to-send copy"}
        </button>

        {copyOpen && (
          <div className="space-y-2 mt-1">
            <div>
              <p className="flex items-center gap-1 text-[11px] font-semibold text-slate-300 mb-1"><Mail size={12} className="text-emerald-400" /> Cover-star invite</p>
              {c.invite_copy ? (
                <CopyStudio value={c.invite_copy} onSave={saveInvite} category="cover_invite" aiContext={`Cover star: ${c.subject_name}${c.business_name ? " of " + c.business_name : ""}. This is the 'you're on the cover of S/ME' invite.`} />
              ) : <p className="text-[11px] text-slate-500">Copy is being drafted…</p>}
            </div>
            <div>
              <p className="flex items-center gap-1 text-[11px] font-semibold text-slate-300 mb-1"><Megaphone size={12} className="text-amber-400" /> Nomination ask</p>
              {c.nomination_copy ? (
                <CopyStudio value={c.nomination_copy} onSave={saveNomination} category="cover_nomination" aiContext={`Cover star: ${c.subject_name}. This asks them to nominate other UK SME owners for a cover.`} />
              ) : <p className="text-[11px] text-slate-500">Copy is being drafted…</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Btn = ({ children, onClick, disabled, icon: Icon, accent, danger, testid }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg transition-all disabled:opacity-50 ${
      accent ? "bg-emerald-500 text-[#04140d] hover:bg-emerald-400"
        : danger ? "border border-red-500/25 text-red-300 hover:bg-red-500/10"
          : "bg-white/[0.06] text-slate-200 hover:bg-white/[0.1]"}`}>
    <Icon size={13} /> {children}
  </button>
);
